import { Alert, List, Stack, TextField, Typography, Button } from "@mui/material"
import { DialogBox } from "lib/@components/dialogBox"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { PagedRepeat } from "lib/@components/paged-repeat"
import { useEffect, useMemo, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { AuthoringScheduleSelectorItem } from "./authoring-schedule-selector-item"
import { SelectAllCheckbox } from "lib/@components/select-all-checkbox"
import { setFromEvent } from "lib/setFromEvent"
import { AuthoringFilter } from "authoring/routes/project/group/overwrite/task-correlator/authoring-filter"
import { StandardSwitch } from "lib/@components/binding/bound-components"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { NoResults } from "lib/@components/no-results/no-results"
import { OkCancel } from "lib/@components/okCancel"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { getProjectScopedSchedules } from "authoring/routes/project/controller/get-project-scoped-schedules"
import { refreshOnlyFor } from "lib/graphql/api"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { compareByCode } from "authoring/lib/compare-by-code"
import { isScheduleEditable } from "./lib/is-schedule-editable"

export function AuthoringScheduleSelectorDialog({
    type,
    transform = (v) => v,
    warning,
    allowMultiple = true,
    existingSelection = [],
    actionsComponent = OkCancel,
    primarySx = {},
}) {
    const { project } = useBoundContext()
    const { id: projectId } = project ?? {}

    const schedulesResult = getProjectScopedSchedules.useResults(
        { projectId, showAll: false },
        refreshOnlyFor(projectId),
        withInitialValue(IS_LOADING)
    )
    const schedulesLoading = schedulesResult === IS_LOADING
    const allSchedules = schedulesLoading ? undefined : schedulesResult
    const [selectedItems, setSelectedItems] = useState([])
    const [initialised, setInitialised] = useState(false) // blocks existing selection from overwriting empty selection after mount
    const [droppedExistingSelectionCount, setDroppedExistingSelectionCount] = useState(0)
    const [search, setSearch] = useState("")
    const [filter, setFilter] = useState("")
    const [showSelected, setShowSelected] = useState(false)
    const filterOptions = ["draft", "published"]
    const scheduleLookup = useMemo(() => allSchedules?.keyBy("id"), [allSchedules])

    const filteredSchedules = useMemo(
        () => (allSchedules ?? []).map(transform).filter(matchesSearch).sort(compareByCode),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allSchedules, search, filter]
    )

    const filteredSelected = useMemo(
        () => (selectedItems ?? []).map(transform).filter(matchesSearch).sort(compareByCode),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedItems, showSelected, search, filter]
    )

    const selectableSchedules = useMemo(() => filteredSchedules.filter(isScheduleEditable), [filteredSchedules])

    const disableSelection = allowMultiple ? false : selectedItems.length >= 1

    const dialogActions = !allowMultiple && selectedItems.length > 0 && (
        <Button onClick={handleClearAll}>Clear Selection</Button>
    )

    useEffect(() => {
        if (initialised || !allSchedules?.length || !existingSelection?.length || !scheduleLookup) return
        const existing = existingSelection.map((id) => scheduleLookup[id]).filter(Boolean)
        setDroppedExistingSelectionCount(existingSelection.length - existing.length)
        setSelectedItems(existing ?? [])
        setInitialised(true)
    }, [allSchedules, scheduleLookup, existingSelection, initialised])

    if (schedulesLoading) return <LoadingScreen />

    return (
        <Bound selectedItems={selectedItems} filter={filter} setFilter={setFilter} filterOptions={filterOptions}>
            <DialogBox
                title={`Add Schedules`}
                value={selectedItems}
                actions={dialogActions}
                data-cy="authoring-schedule-selector-dialog"
                Actions={actionsComponent}
            >
                <Stack spacing={2} mt={2}>
                    {droppedExistingSelectionCount > 0 && (
                        <Alert severity="warning" data-cy="dropped-existing-selection-warning">
                            <Typography variant="body2">
                                {buildDroppedSelectionMessage(droppedExistingSelectionCount)}
                            </Typography>
                        </Alert>
                    )}
                    <Typography>The following {type} can be selected:</Typography>

                    <TextField
                        value={search}
                        onChange={setFromEvent(setSearch)}
                        fullWidth
                        autoFocus
                        variant="standard"
                        label="Search..."
                        data-cy="authoring-schedule-selector-search"
                    />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <StandardSwitch
                            value={showSelected}
                            label={`Show Selected Only ${selectedItems.length ? `(${selectedItems.length})` : ""}`}
                            onChange={handleShowSelected}
                            data-cy="show-selected-toggle"
                        />
                        <AuthoringFilter />
                    </Stack>
                    <If truthy={filteredSchedules && filteredSchedules.length > 0}>
                        <List dense>
                            {allowMultiple && selectableSchedules.length > 1 && (
                                <SelectAllCheckbox data={selectableSchedules} onSelect={handleSelectAll} />
                            )}
                            <If truthy={!showSelected}>
                                <PagedRepeat
                                    list={filteredSchedules}
                                    item={
                                        <AuthoringScheduleSelectorItem
                                            onSelect={handleToggle}
                                            disabled={disableSelection}
                                            primarySx={primarySx}
                                        />
                                    }
                                    showTop
                                />
                                <Else>
                                    <PagedRepeat
                                        list={filteredSelected}
                                        item={
                                            <AuthoringScheduleSelectorItem
                                                onSelect={handleToggle}
                                                disabled={disableSelection}
                                            />
                                        }
                                        fallback={<NoResults>No Selected Schedules Found</NoResults>}
                                        showTop
                                    />
                                </Else>
                            </If>
                        </List>
                        {!!warning && (
                            <Alert severity="warning">
                                <Typography variant="body2">{warning}</Typography>
                            </Alert>
                        )}
                        <Else>
                            <If truthy={!allSchedules?.length}>
                                <NoResults>No schedules have been added to this work package.</NoResults>
                                <Else>
                                    <NoResults />
                                </Else>
                            </If>
                        </Else>
                    </If>
                </Stack>
            </DialogBox>
        </Bound>
    )

    function buildDroppedSelectionMessage(count) {
        const schedule = count === 1 ? "schedule" : "schedules"
        const verb = count === 1 ? "is" : "are"
        return `${count} previously-selected ${schedule} ${verb} no longer available and ${
            count === 1 ? "was" : "were"
        } removed from this selection.`
    }

    function matchesSearch({ title, code, id }) {
        const searchLower = search.toLowerCase()
        return (
            title.toLowerCase().includes(searchLower) ||
            code?.toLowerCase().includes(searchLower) ||
            id?.toString().includes(searchLower)
        )
    }

    function handleToggle(item) {
        if (!isScheduleEditable(item)) return
        const unselect = selectedItems.some(({ id }) => id === item.id)
        if (unselect) {
            setSelectedItems((c) => c.filter(({ id }) => id !== item.id))
        } else {
            setSelectedItems((c) => [...c, item])
        }
    }

    function handleClearAll() {
        setSelectedItems([])
    }
    function handleSelectAll(allChecked) {
        if (allChecked) {
            setSelectedItems([])
        } else {
            setSelectedItems(filteredSchedules.filter(isScheduleEditable))
        }
    }
    function handleShowSelected() {
        setShowSelected(!showSelected)
    }
}
